// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import translate from "@insite/client-framework/Translate";
import { ProductDto, WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import Download from "@store/Library/Components/Icons/Download";
import Typography from "@store/MobiusOverrides/Typography/Typography";

import AccordionSection from "@insite/mobius/AccordionSection";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import FileUploadField from "@store/Library/Components/BuyingList/FileUploadField";
import Button, { ButtonIcon } from "@store/MobiusOverrides/Button/Button";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface WishListDetailsUploadAfileStyles {
    addItemsAccordion?: AccordionPresentationProps;
    uploadItemsLink?: LinkPresentationProps;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
}
export const styles: WishListDetailsUploadAfileStyles = {};

const mapStateToProps = (state: ApplicationState) => ({
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    loadWishListIfNeeded,
});
const mapDispatchToProps = {
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const downloadTemplateHandler = () => {
    window.open(`${window.location.origin}/UserFiles/templates/ProductUploadTemplate.xlsx`);
};

const MyListDetailsUploadAfile = ({ wishListDataView, addToWishList }: Props) => {
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const toasterContext = useToaster();

    const wishList = wishListDataView.value;
    if (!wishList) {
        return null;
    }
    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };
    const addItemsToListClickHandler = () => {
        setAddItemsToListIsOpen(!addItemsToListIsOpen);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };
    const uploadProductsHandler = async (products: ProductDto[]) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        await addToWishList({ productInfos, selectedWishList: wishList });
        setUploadItemsModalIsOpen(false);
        loadWishListIfNeeded({ wishListId: wishList.id });
    };

    const selectProductHandler = async (productInfo: ProductInfo) => {
        await addToWishList({ productInfos: [productInfo], selectedWishList: wishList });
        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;
    if (!canEditWishList) {
        return null;
    }

    return (
        <>
            <Accordion headingLevel={2} {...styles.addItemsAccordion}>
                <AccordionSection
                    title={translate("Add Items To List")}
                    expanded={addItemsToListIsOpen}
                    onTogglePanel={addItemsToListClickHandler}
                >
                    <Link
                        {...styles.uploadItemsLink}
                        onClick={uploadItemsClickHandler}
                        data-test-selector="myListDetails_uploadItems"
                    >
                        {translate("Upload Items")}
                    </Link>
                    <ProductSelector
                        selectButtonTitle={translate("Add to List")}
                        skipInventoryValidation={true}
                        denyFixedConfiguration={true}
                        onSelectProduct={selectProductHandler}
                        productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                        productIsUnavailableMessage={siteMessage("Product_NotFound")}
                        extendedStyles={styles.productSelectorStyles}
                    />
                </AccordionSection>
            </Accordion>
            <Modal
                {...styles.uploadItemsModal}
                headline={translate("Upload Items To List")}
                isOpen={uploadItemsModalIsOpen}
                handleClose={uploadItemsModalCloseHandler}
                data-test-selector="myListDetails_uploadItemsToListModal"
            >
                <GridContainer>
                    <GridItem
                        css={css`
                            margin-bottom: 0;
                            flex-direction: column;
                            gap: 2.5rem;
                            justify-content: center;
                            align-items: center;
                        `}
                        width={[12, 12, 6, 6, 6]}
                    >
                        <Typography
                            css={css`
                                font-size: 1.125rem;
                                max-width: 75%;
                            `}
                        >
                            {translate(
                                `Upload an Excel (.xls or .xlsx) or CSV (Comma-Separated Values) file to quickly add product(s) to your list.`,
                            )}
                        </Typography>
                        <Button
                            onClick={downloadTemplateHandler}
                            icon={{
                                src: Download,
                                position: "left",
                            }}
                            css={css`
                                display: flex;
                                align-items: center;
                                margin-bottom: ${pxToRem(15)};
                                ${ButtonIcon} {
                                    color: ${getColor("primary")};
                                }
                                > * {
                                    top: 0 !important;
                                }
                            `}
                            variant="tertiary"
                        >
                            {translate("Download Template")}
                        </Button>
                    </GridItem>
                    <GridItem width={[12, 12, 6, 6, 6]}>
                        <FileUploadField onUpload={uploadProductsHandler} />
                    </GridItem>
                </GridContainer>
            </Modal>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListDetailsUploadAfile),
    definition: {
        group: "My Lists Details",
        displayName: "Upload a file",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
